import { useState } from 'react';
import { gql, useMutation } from '@apollo/client';
import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  FormHelperText,
  MenuItem,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import { User } from 'src/models/user';
import {
  ORG_TYPES,
  OrgType,
  orgTypeLabel
} from 'src/models/professionalAccess';
import GroupTrialsSection from './groupTrials/GroupTrialsSection';

// Admin-service mutation (SEN-6029). Upserts the organization profile for a
// target user — assumed contract:
//   UpsertOrganization(input: { userId, organizationName, organizationType,
//   organizationTypeOther }): Organization
const UPSERT_ORGANIZATION = gql`
  mutation UpsertOrganization($input: UpsertOrganizationInput!) {
    UpsertOrganization(input: $input) {
      id
      organizationName
      organizationType
      organizationTypeOther
    }
  }
`;

type Props = {
  user: User;
  refreshUser: (email: string) => void;
};

const DetailRow = ({ label, value }: { label: string; value: string }) => (
  <Box sx={{ display: 'flex', gap: 1 }}>
    <Typography variant="body2" color="textSecondary" sx={{ minWidth: 180 }}>
      {label}
    </Typography>
    <Typography variant="body2">{value || '—'}</Typography>
  </Box>
);

const UserOrganization = ({ user, refreshUser }: Props) => {
  const organization = user.organization;
  const hasOrganization = !!organization?.id;

  const [editing, setEditing] = useState(false);
  const [organizationName, setOrganizationName] = useState(
    organization?.organizationName ?? ''
  );
  const [organizationType, setOrganizationType] = useState<OrgType>(
    organization?.organizationType ?? 'CLINIC_OR_HUB'
  );
  const [organizationTypeOther, setOrganizationTypeOther] = useState(
    organization?.organizationTypeOther ?? ''
  );
  const [validationError, setValidationError] = useState<string | undefined>();

  const [executeUpsert, { loading, error }] = useMutation(UPSERT_ORGANIZATION, {
    errorPolicy: 'all'
  });

  const startEditing = () => {
    setOrganizationName(organization?.organizationName ?? '');
    setOrganizationType(organization?.organizationType ?? 'CLINIC_OR_HUB');
    setOrganizationTypeOther(organization?.organizationTypeOther ?? '');
    setValidationError(undefined);
    setEditing(true);
  };

  const handleSubmit = async () => {
    if (!organizationName.trim()) {
      setValidationError('Organization name is required.');
      return;
    }
    if (organizationType === 'OTHER' && !organizationTypeOther.trim()) {
      setValidationError(
        'Please describe the organization type when "Other" is selected.'
      );
      return;
    }
    setValidationError(undefined);

    const result = await executeUpsert({
      variables: {
        input: {
          userId: user.id,
          organizationName: organizationName.trim(),
          organizationType,
          // Only meaningful for OTHER; clear it otherwise.
          organizationTypeOther:
            organizationType === 'OTHER' ? organizationTypeOther.trim() : ''
        }
      }
    });

    if (result.data?.UpsertOrganization) {
      setEditing(false);
      refreshUser(user.email);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2
        }}
      >
        <Typography variant="h5">Organization</Typography>
        {!editing && (
          <Button variant="contained" onClick={startEditing}>
            {hasOrganization ? 'Edit organization' : 'Add organization'}
          </Button>
        )}
      </Box>

      {!editing && !hasOrganization && (
        <Typography variant="body2" color="textSecondary">
          This user is not connected to an organization.
        </Typography>
      )}

      {!editing && hasOrganization && (
        <Card variant="outlined">
          <CardContent>
            <Stack spacing={0.5}>
              <DetailRow
                label="Name"
                value={organization?.organizationName ?? ''}
              />
              <DetailRow
                label="Type"
                value={orgTypeLabel(organization?.organizationType)}
              />
              {organization?.organizationType === 'OTHER' && (
                <DetailRow
                  label="Type (other)"
                  value={organization?.organizationTypeOther ?? ''}
                />
              )}
            </Stack>
          </CardContent>
        </Card>
      )}

      {editing && (
        <Card variant="outlined">
          <CardContent>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>
              {hasOrganization ? 'Edit organization' : 'Add organization'}
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Stack spacing={3}>
              <TextField
                label="Organization name"
                size="small"
                fullWidth
                required
                value={organizationName}
                onChange={(e) => setOrganizationName(e.target.value)}
              />
              <TextField
                select
                label="Organization type"
                size="small"
                fullWidth
                value={organizationType}
                onChange={(e) =>
                  setOrganizationType(e.target.value as OrgType)
                }
              >
                {ORG_TYPES.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </TextField>
              {organizationType === 'OTHER' && (
                <TextField
                  label="Organization type (other)"
                  size="small"
                  fullWidth
                  value={organizationTypeOther}
                  onChange={(e) => setOrganizationTypeOther(e.target.value)}
                />
              )}

              {(validationError || error) && (
                <FormHelperText error>
                  {validationError ?? error?.message}
                </FormHelperText>
              )}

              <Box
                sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}
              >
                <Button onClick={() => setEditing(false)} disabled={loading}>
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  disabled={loading}
                >
                  {loading ? 'Saving…' : 'Save'}
                </Button>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      )}

      <GroupTrialsSection organizationId={organization?.id} />
    </Box>
  );
};

export default UserOrganization;
